import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import UnreadMessagesBanner from '@/components/UnreadMessagesBanner';
import MothersDayTheme from '@/components/MothersDayTheme';
import DogDayTheme from '@/components/DogDayTheme';
import Login from '@/pages/Login';
import Welcome from '@/pages/Welcome';
import Dashboard from '@/pages/Dashboard';
import NewPayment from '@/pages/NewPayment';
import RegisterDeposit from '@/pages/RegisterDeposit';
import PaymentDetail from '@/pages/PaymentDetail';
import AdminUsers from '@/pages/AdminUsers';
import AdminProviders from '@/pages/AdminProviders';
import AdminStores from '@/pages/AdminStores';
import AdminBoletas from '@/pages/AdminBoletas';
import AdminBancos from '@/pages/AdminBancos';
import AdminSettings from '@/pages/AdminSettings';
import AdminWhatsApp from '@/pages/AdminWhatsApp';
import Reports from '@/pages/Reports';
import BankValidation from '@/pages/BankValidation';
import MacrobaseReport from '@/pages/MacrobaseReport';
import ProviderVisits from '@/pages/ProviderVisits';
import ContadoVencidosReport from '@/pages/ContadoVencidosReport';
import DetailedReport from '@/pages/DetailedReport';
import ComprasProveedorReport from '@/pages/ComprasProveedorReport';
import Adelantos from '@/pages/Adelantos';
import AdelantoDetail from '@/pages/AdelantoDetail';
import FlujoSync from '@/pages/FlujoSync';
import DesignPreview from '@/pages/DesignPreview';
import AppShell from '@/components/AppShell';
import { Toaster } from 'react-hot-toast';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="text-lg font-medium text-[#2c57c5]">Cargando...</div>
      </div>
    );
  }
  
  return user ? (
    <AppShell>
      <MothersDayTheme />
      <DogDayTheme />
      <UnreadMessagesBanner />
      <div className="pt-0">{children}</div>
    </AppShell>
  ) : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="text-lg font-medium text-[#2c57c5]">Cargando...</div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" />;
  
  return children;
};

const ManagementRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="text-lg font-medium text-[#2c57c5]">Cargando...</div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  // Allow admin, gerencia, supervisor, encargada_compras, asistente_admin
  if (!['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'].includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Route only for admin and gerencia
const AdminGerenciaRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="text-lg font-medium text-[#2c57c5]">Cargando...</div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  // Only admin and gerencia
  if (!['admin', 'gerencia'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
        <BrowserRouter>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/design-preview" element={<DesignPreview />} />
          <Route path="/welcome" element={
            <PrivateRoute>
              <Welcome />
            </PrivateRoute>
          } />
          <Route path="/dashboard" element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          } />
          <Route path="/payments/new" element={
            <PrivateRoute>
              <NewPayment />
            </PrivateRoute>
          } />
          <Route path="/deposits/register" element={
            <PrivateRoute>
              <RegisterDeposit />
            </PrivateRoute>
          } />
          <Route path="/payments/:id" element={
            <PrivateRoute>
              <PaymentDetail />
            </PrivateRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          } />
          <Route path="/admin/providers" element={
            <ManagementRoute>
              <AdminProviders />
            </ManagementRoute>
          } />
          <Route path="/admin/stores" element={
            <ManagementRoute>
              <AdminStores />
            </ManagementRoute>
          } />
          <Route path="/admin/boletas" element={
            <AdminGerenciaRoute>
              <AdminBoletas />
            </AdminGerenciaRoute>
          } />
          <Route path="/admin/bancos" element={
            <ManagementRoute>
              <AdminBancos />
            </ManagementRoute>
          } />
          <Route path="/admin/settings" element={
            <AdminRoute>
              <AdminSettings />
            </AdminRoute>
          } />
          <Route path="/admin/whatsapp" element={
            <ManagementRoute>
              <AdminWhatsApp />
            </ManagementRoute>
          } />
          <Route path="/reports" element={
            <ManagementRoute>
              <Reports />
            </ManagementRoute>
          } />
          <Route path="/bank-validation" element={
            <ManagementRoute>
              <BankValidation />
            </ManagementRoute>
          } />
          <Route path="/macrobase-report" element={
            <ManagementRoute>
              <MacrobaseReport />
            </ManagementRoute>
          } />
          <Route path="/provider-visits" element={
            <ManagementRoute>
              <ProviderVisits />
            </ManagementRoute>
          } />
          <Route path="/contado-vencidos" element={
            <ManagementRoute>
              <ContadoVencidosReport />
            </ManagementRoute>
          } />
          <Route path="/reporte-compras" element={
            <PrivateRoute>
              <ComprasProveedorReport />
            </PrivateRoute>
          } />
          <Route path="/reporte-detallado" element={
            <ManagementRoute>
              <DetailedReport />
            </ManagementRoute>
          } />
          <Route path="/adelantos" element={
            <ManagementRoute>
              <Adelantos />
            </ManagementRoute>
          } />
          <Route path="/adelantos/:id" element={
            <ManagementRoute>
              <AdelantoDetail />
            </ManagementRoute>
          } />
          <Route path="/flujo-sync" element={
            <ManagementRoute>
              <FlujoSync />
            </ManagementRoute>
          } />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ErrorBoundary>
  );
}

export default App;
